import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import {
    MenuBook as MenuBookIcon,
    ExpandMore as ExpandMoreIcon,
    Help as HelpIcon,
    Code as CodeIcon,
    Security as SecurityIcon,
    Update as UpdateIcon
} from '@mui/icons-material';

export default function DocumentationPage() {
    const sections = [
        {
            title: 'Guide d\'utilisation',
            icon: <HelpIcon />,
            color: 'primary',
            items: [
                'Comment ajouter un nouveau client',
                'Gestion des applications',
                'Création et suivi des tickets',
                'Utilisation du tableau de bord'
            ]
        },
        {
            title: 'Documentation technique',
            icon: <CodeIcon />,
            color: 'info',
            items: [
                'API REST disponible',
                'Intégrations tierces',
                'Webhooks et callbacks',
                'Format des données'
            ]
        },
        {
            title: 'Sécurité',
            icon: <SecurityIcon />,
            color: 'warning',
            items: [
                'Authentification OAuth2',
                'Gestion des permissions',
                'Audit et logs',
                'Bonnes pratiques'
            ]
        },
        {
            title: 'Notes de version',
            icon: <UpdateIcon />,
            color: 'success',
            items: [
                'v1.2.0 - Nouvelles fonctionnalités ERP',
                'v1.1.5 - Corrections de bugs',
                'v1.1.0 - Interface utilisateur améliorée',
                'v1.0.0 - Version initiale'
            ]
        }
    ];

    const faqs = [
        {
            question: 'Comment réinitialiser mon mot de passe ?',
            answer: 'Utilisez la fonction "Mot de passe oublié" sur la page de connexion. Un email de réinitialisation vous sera envoyé.'
        },
        {
            question: 'Comment ajouter un nouveau membre à mon équipe ?',
            answer: 'Allez dans Paramètres > Utilisateurs et cliquez sur "Inviter un utilisateur". Définissez ses permissions selon son rôle.'
        },
        {
            question: 'Mes données sont-elles sauvegardées automatiquement ?',
            answer: 'Oui, toutes vos données sont sauvegardées automatiquement et en continu. Des sauvegardes complètes sont effectuées quotidiennement.'
        },
        {
            question: 'Comment exporter mes données ?',
            answer: 'Chaque module dispose d\'une fonction d\'export en CSV/Excel accessible via le menu "Actions" ou le bouton "Exporter".'
        },
        {
            question: 'Comment contacter le support technique ?',
            answer: 'Utilisez l\'onglet "Support" pour créer un ticket, ou contactez-nous directement via support@alais-studio.fr'
        }
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                Documentation
            </Typography>

            {/* Introduction */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MenuBookIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        Bienvenue dans la documentation
                    </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                    Cette documentation vous aidera à tirer le meilleur parti de votre système ERP/CRM.
                    Vous trouverez ici des guides d'utilisation, de la documentation technique, et des réponses aux questions fréquentes.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Dernière mise à jour : {new Date().toLocaleDateString()}
                </Typography>
            </Paper>

            {/* Sections principales */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {sections.map((section, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    {React.cloneElement(section.icon, { sx: { color: `${section.color}.main`, mr: 1 } })}
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {section.title}
                                    </Typography>
                                </Box>
                                <List dense>
                                    {section.items.map((item, itemIndex) => (
                                        <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemText
                                                primary={item}
                                                primaryTypographyProps={{
                                                    variant: 'body2',
                                                    sx: { cursor: 'pointer', '&:hover': { color: 'primary.main' } }
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* FAQ */}
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                    Questions fréquemment posées (FAQ)
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                    Trouvez rapidement des réponses aux questions les plus courantes.
                </Typography>

                {faqs.map((faq, index) => (
                    <Accordion key={index}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 500 }}>
                                {faq.question}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2" color="text.secondary">
                                {faq.answer}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Paper>

            {/* Contact */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                    Besoin d'aide supplémentaire ?
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Notre équipe support est là pour vous aider
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                        label="support@alais-studio.fr"
                        color="primary"
                        variant="outlined"
                        sx={{ bgcolor: 'background.paper' }}
                    />
                    <Chip
                        label="Créer un ticket"
                        color="primary"
                        sx={{ bgcolor: 'primary.main', color: 'white' }}
                    />
                    <Chip
                        label="Guide vidéo"
                        color="info"
                        variant="outlined"
                        sx={{ bgcolor: 'background.paper' }}
                    />
                </Box>
            </Box>
        </Box>
    );
}